import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { canSellPremium } from '@/lib/appUrl';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, Sparkles, Zap, BarChart3, Building2,
  CreditCard, ShieldCheck, ArrowRight, Crown, Star,
} from 'lucide-react';

/**
 * Public landing page targeting facility owners — `/business`.
 * Goal: convert sport center managers into self-onboarded facility accounts.
 */
const BusinessLandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const goToOnboarding = () => {
    if (isAuthenticated) navigate('/facilities/new');
    else navigate('/?from=business');
  };

  return (
    <div className="safe-public-page bg-background">
      {/* Top header (slim) */}
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">L</div>
            LeagueHub
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground">Home</Link>
            <Button onClick={goToOnboarding} size="sm" className="bg-gradient-to-br from-emerald-500 to-emerald-600">
              Iscrivi struttura
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-semibold mb-5">
            <Sparkles className="h-3 w-3" />
            NOVITÀ · gratis al lancio
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
            Riempi i tuoi campi.<br />
            <span className="opacity-90">Senza fatica.</span>
          </h1>
          <p className="mt-5 text-lg opacity-90 max-w-2xl mx-auto">
            Pubblica il tuo centro su LeagueHub e raggiungi <strong>centinaia di giocatori amatoriali</strong> della tua città.
            Zero costi fissi, solo commissione sulle prenotazioni completate.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Button
              size="lg"
              onClick={goToOnboarding}
              className="bg-white text-emerald-700 hover:bg-white/95 font-bold px-8 py-6 text-base shadow-lg"
            >
              <Building2 className="h-5 w-5 mr-2" />
              Iscrivi la tua struttura
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('come-funziona')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white font-medium px-8 py-6 text-base"
            >
              Come funziona
            </Button>
          </div>
          <div className="mt-6 text-sm opacity-80 flex flex-wrap justify-center gap-x-6 gap-y-1">
            <span>⏱ Setup in 5 minuti</span>
            <span>🔒 Pagamenti via Stripe</span>
            <span>💬 Supporto WhatsApp</span>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-2xl border bg-card p-6">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center text-2xl mb-3">📈</div>
            <h3 className="font-bold text-lg">Più prenotazioni</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Le leghe amatoriali della tua zona prenotano direttamente i tuoi slot — anche fuori dagli orari di punta.
            </p>
          </div>
          <div className="rounded-2xl border bg-card p-6">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-950/40 flex items-center justify-center text-2xl mb-3"><Zap className="h-6 w-6 text-purple-600" /></div>
            <h3 className="font-bold text-lg">Zero gestione</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Calendar visivo, pagamenti automatici via Stripe, payouts settimanali al tuo IBAN. Niente più WhatsApp infiniti.
            </p>
          </div>
          <div className="rounded-2xl border bg-card p-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center text-2xl mb-3"><BarChart3 className="h-6 w-6 text-emerald-600" /></div>
            <h3 className="font-bold text-lg">Analytics chiari</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Vedi quali slot sono più richiesti, fatturato mensile, top clienti. Decidi prezzi data-driven.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="come-funziona" className="bg-muted/40 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-center mb-2">Come funziona</h2>
          <p className="text-center text-muted-foreground mb-10">Sei online in tre passi.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: 1, t: 'Iscrivi la struttura', d: '5 minuti: dati, foto, campi, prezzi, IBAN. Self-service, niente call.' },
              { n: 2, t: 'Sei online subito', d: 'Pagina pubblica brandizzata + sei nel marketplace. Indicizzata su Google.' },
              { n: 3, t: 'Incassi', d: 'Pagamenti istantanei Stripe → payouts settimanali al tuo IBAN. Trasparenti.' },
            ].map((s) => (
              <div key={s.n} className="bg-card rounded-2xl border p-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold mb-3">{s.n}</div>
                <div className="font-semibold text-lg">{s.t}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-2">Prezzi onesti</h2>
        <p className="text-center text-muted-foreground mb-10">Niente abbonamenti nascosti.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free / Standard */}
          <div className="rounded-2xl border-2 bg-card p-6">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Standard</div>
            <div className="text-4xl font-bold">10%<span className="text-base text-muted-foreground font-normal"> commissione</span></div>
            <div className="text-sm text-muted-foreground mt-1">Solo sulle prenotazioni completate</div>
            <ul className="mt-5 space-y-2 text-sm">
              {[
                'Pagina pubblica brandizzata',
                'Marketplace strutture',
                'Calendar e prenotazioni',
                'Pagamenti Stripe + payouts settimanali',
                'Analytics base',
                'Supporto via email',
              ].map((f) => (
                <li key={f} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <Button onClick={goToOnboarding} variant="secondary" className="mt-6 w-full">
              Inizia gratis <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Premium */}
          {canSellPremium() && (
          <div className="rounded-2xl border-2 border-purple-500 bg-gradient-to-br from-purple-600 to-indigo-600 text-white p-6 relative overflow-hidden">
            <div className="absolute top-3 right-3 text-xs bg-white/20 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
              <Crown className="h-3 w-3" /> Featured
            </div>
            <div className="text-xs font-semibold uppercase mb-1 opacity-80">Premium</div>
            <div className="text-4xl font-bold">€29<span className="text-base font-normal opacity-80">/mese + 7%</span></div>
            <div className="text-sm opacity-80 mt-1">Commissione ridotta + visibilità extra</div>
            <ul className="mt-5 space-y-2 text-sm">
              {[
                'Tutto di Standard',
                'Top results in marketplace',
                'Badge "Verificato"',
                'Analytics avanzate (heatmap, forecast)',
                'Promozioni illimitate',
                'Priorità supporto WhatsApp',
              ].map((f) => (
                <li key={f} className="flex gap-2"><Star className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <Button onClick={goToOnboarding} className="mt-6 w-full bg-white text-purple-700 hover:bg-white/95 font-bold">
              Prova Premium 30gg gratis <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          )}
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-muted/40 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="h-8 w-8 text-emerald-600" />
              <span className="font-medium">Pagamenti sicuri</span>
              <span className="text-xs text-muted-foreground">Stripe certified</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <CreditCard className="h-8 w-8 text-blue-600" />
              <span className="font-medium">Payouts veloci</span>
              <span className="text-xs text-muted-foreground">Settimanali sull'IBAN</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Zap className="h-8 w-8 text-amber-600" />
              <span className="font-medium">Setup 5 minuti</span>
              <span className="text-xs text-muted-foreground">Self-service</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <BarChart3 className="h-8 w-8 text-purple-600" />
              <span className="font-medium">Analytics inclusi</span>
              <span className="text-xs text-muted-foreground">Anche nel piano Standard</span>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="text-3xl font-bold">Pronto a riempire i tuoi campi?</h2>
        <p className="text-muted-foreground mt-2">5 minuti per essere online. Zero impegno.</p>
        <Button
          size="lg"
          onClick={goToOnboarding}
          className="mt-6 bg-gradient-to-br from-emerald-500 to-emerald-600 px-10 py-6 text-base font-bold shadow-lg"
        >
          <Building2 className="h-5 w-5 mr-2" />
          Inizia ora — è gratis
        </Button>
      </section>

      <footer className="border-t bg-card py-6 text-center text-xs text-muted-foreground">
        © LeagueHub · <Link to="/" className="hover:underline">Torna al sito</Link>
      </footer>
    </div>
  );
};

export default BusinessLandingPage;
